import { create } from 'zustand';
import type { AutoWorkflowPlan } from '@sr-test-wizard/shared';
import { autoWorkflowApi, type AutoWorkflowStatusResponse } from '../lib/autoWorkflowApi';

interface AutoWorkflowState {
  runId: string | null;
  status: AutoWorkflowStatusResponse | null;
  load: (runId: string) => Promise<void>;
  applyProgress: (msg: {
    runId: string;
    step: string;
    status: string;
    message?: string;
    running: boolean;
  }) => void;
  start: (runId: string, plan: AutoWorkflowPlan) => Promise<void>;
  stop: (runId: string) => Promise<void>;
  clear: () => void;
}

const empty = (): AutoWorkflowStatusResponse => ({
  running: false,
  step: null,
  message: null,
  error: null,
  plan: null,
  preconditionsReady: true,
  preconditionsReason: null,
});

export const useAutoWorkflowStore = create<AutoWorkflowState>((set, get) => ({
  runId: null,
  status: null,

  async load(runId) {
    set({ runId });
    try {
      const data = await autoWorkflowApi.status(runId);
      if (get().runId !== runId) return;
      set({ status: data });
    } catch {
      if (get().runId !== runId) return;
      set({ status: empty() });
    }
  },

  applyProgress(msg) {
    if (get().runId && msg.runId !== get().runId) return;
    set((s) => ({
      status: {
        ...(s.status ?? empty()),
        running: msg.running,
        step: msg.step === 'idle' ? s.status?.step ?? null : msg.step,
        message: msg.message ?? s.status?.message ?? null,
        error: msg.status === 'failed' ? msg.message ?? s.status?.error ?? null : null,
      },
    }));
  },

  async start(runId, plan) {
    set({ runId });
    await autoWorkflowApi.start(runId, plan);
    await get().load(runId);
  },

  async stop(runId) {
    await autoWorkflowApi.stop(runId);
  },

  clear() {
    set({ runId: null, status: null });
  },
}));
